import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { coursesAPI, monetizationAPI } from '../../services/api'
import { completeCardPayment } from '../../services/cardPayment'
import type { Course, PaymentMethod } from '../../types'
import { apiErrorMessage } from '../../utils/apiError'

/**
 * Buying a course sold on its own: pick a saved card, pay once, lifetime
 * access. When the bank asks for approval (3-D Secure) its step opens here;
 * the enrolment itself follows Stripe's confirmation on the server
 * (backend services/course_sales), so `onBought` reloads to see it.
 */
const CourseBuy = ({ course, onBought }: { course: Pick<Course, 'id' | 'title' | 'price'>; onBought: () => void }) => {
  const [methods, setMethods] = useState<PaymentMethod[] | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [buying, setBuying] = useState(false)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  useEffect(() => {
    let live = true
    monetizationAPI
      .getPaymentMethods()
      .then((list: PaymentMethod[]) => {
        if (!live) return
        setMethods(list)
        const preferred = list.find((m) => m.is_default) || list[0]
        setSelected(preferred ? preferred.id : null)
      })
      .catch(() => live && setMethods([]))
    return () => {
      live = false
    }
  }, [])

  const buy = async () => {
    if (!selected) return
    setBuying(true)
    setMessage(null)
    try {
      const charge = await coursesAPI.purchase(course.id, selected)
      const outcome = await completeCardPayment(charge)
      if (outcome.status === 'failed') {
        setMessage({ tone: 'error', text: outcome.message })
        return
      }
      setMessage({
        tone: 'ok',
        text: charge.enrolled ? 'Bought. The lessons are open.' : 'Approved. The course opens in a moment.',
      })
      onBought()
    } catch (error) {
      setMessage({ tone: 'error', text: apiErrorMessage(error, "Couldn't buy the course.") })
    } finally {
      setBuying(false)
    }
  }

  return (
    <div className="space-y-2" data-testid="course-buy">
      {methods === null ? (
        <p className="text-sm text-gray-400">Loading your cards…</p>
      ) : methods.length === 0 ? (
        <p className="text-sm text-gray-400" data-testid="course-buy-no-card">
          Add a card in{' '}
          <Link to="/subscriptions" className="text-sky-300 underline">
            Subscriptions
          </Link>{' '}
          to buy this course.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="course-buy-card">
            Card
          </label>
          <select
            id="course-buy-card"
            value={selected ?? ''}
            onChange={(e) => setSelected(e.target.value)}
            className="rounded-md border border-gray-600 bg-gray-800 px-2 py-2 text-sm text-gray-100"
          >
            {methods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.brand || 'Card'} ****{m.last4}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={buy}
            disabled={buying || !selected}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            data-testid="course-buy-button"
          >
            {buying ? 'Paying…' : `Buy for $${course.price.toFixed(2)}`}
          </button>
        </div>
      )}
      {message && (
        <p
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={`text-sm ${message.tone === 'error' ? 'text-amber-300' : 'text-success-500'}`}
          data-testid="course-buy-message"
        >
          {message.text}
        </p>
      )}
    </div>
  )
}

export default CourseBuy
